import * as React from 'react'
import styled from 'styled-components'
import assert from '../../../../common/src/assert'
import Button from '../../../../common/src/components/Button'
import { Dropdown, ListItem } from '../../../../common/src/components/Dropdown'
import Icon, { propertyTypeToIconName } from '../../../../common/src/components/Icon'
import Modal from '../../../../common/src/components/Modal'
import TableMultiselect from '../../../../common/src/components/TableMultiselect'
import { Spacer, BodyTextSpan, IntroText, AlignRight, EllipsisSpan } from '../../../../common/src/components/core'
import { Colors } from '../../../../common/src/constants'
import { TestIds } from '../../../../common/src/data/testids'
import { dateMDShortFmt } from '../../../../common/src/date'
import { titleCase } from '../../../../common/src/format'
import { getOnlyElement } from '../../../../common/src/func'
import { ActionName, LeasePropertyType, QuickActionResult, SendQueue } from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { Field } from '../components/EditBox'
import EditBoxes from '../components/EditBoxes'

/**
 * A single nearby, showable floorplan. The backend gives us a list of these.
 */
export interface CrossSellOption {
  taskId: number
  taskName: string
  propertyType: LeasePropertyType
  floorplan: {
    bedrooms: number
    bathrooms?: number
    sqft: number
    summary: {
      minRentUSD: number
      availableOn: string
      showableOn: string
    }
  }
  address: {
    street: string
    city: string
    state: string
  }
  distanceMi: number
  policy: {
    policyTexts: Record<PolicyText, string>
  }
}

/**
 * The best nearby, showable floorplan for a given task.
 *
 * The Cross Sell Search table shows these tasks, and the operator picks which tasks to cross-sell to.
 *
 * "Best" means cheapest subject to the lead's constraints (on beds, baths, sqft, etc).
 */
interface TaskOption {
  taskId: number
  propertyType: LeasePropertyType
  taskName: string
  address: CrossSellOption['address']
  distanceMi: number
  bestCSO: CrossSellOption
  beds: Range
  baths: Range
  rent: Range
}

interface CrossSellFilters {
  minBeds?: number
  minBaths?: number
  maxRent?: number
  maxAvailableOn?: string
}

interface Props {
  options: CrossSellOption[]
  onChoose: (r: QuickActionResult) => void
  onCancel: () => void
}

interface State {
  filters: CrossSellFilters
  policyCol: string
  selection: Set<string | number>
}

interface Range {
  min?: number
  max?: number
}

export default class CrossSellSearchModal extends React.PureComponent<Props, State> {
  state = { selection: new Set(), filters: {}, policyCol: 'PETS' } as State

  render() {
    const { options, onCancel } = this.props
    const { selection, filters, policyCol } = this.state

    const filteredFloorplanOptions = filterFloorplans(options, filters)
    const taskOptions = cheapestFloorplanForEachTask(filteredFloorplanOptions)
    taskOptions.sort((a, b) => a.distanceMi - b.distanceMi)
    const nSelected = taskOptions.filter(to => selection.has(this.getRowKey(to))).length

    return (
      <Modal title="Cross Sell Search" width={1060} onClose={onCancel} data-testid={TestIds.crossSellSearchModal}>
        <EditBoxes displayType="small" fields={filterFields} model={filters} onEdit={this.editFilters} />
        <Spacer width={16} />
        <Dropdown
          items={policyOptions}
          onSelectedItemChange={this.setPolicyCol}
          selectedItemId={policyCol}
          width={160}
        />
        <Spacer width={16} />
        <BodyTextSpan>
          {filteredFloorplanOptions.length} matching floorplans at {taskOptions.length} nearby properties.
        </BodyTextSpan>
        <Spacer height={16} />
        {taskOptions.length === 0 && <IntroText>No matches</IntroText>}
        {taskOptions.length > 0 && (
          <TableMultiselect
            headers={this.headers}
            widths={this.widths}
            columns={this.cols}
            rows={taskOptions}
            onChange={this.select}
            keyFn={this.getRowKey}
            dataCellComponent={Cell}
          />
        )}
        <Spacer height={16} />
        <AlignRight>
          <Button onClick={this.finish} disabled={nSelected === 0 || nSelected > 5}>
            {nSelected < 2 && 'Cross sell'}
            {nSelected >= 2 && nSelected <= 5 && `Cross sell ${nSelected} options`}
            {nSelected > 5 && 'Max 5 options'}
          </Button>
        </AlignRight>
      </Modal>
    )
  }

  private setPolicyCol = (item: ListItem<string>) => this.setState({ policyCol: item.id })

  private getRowKey = (o: TaskOption) => o.taskId

  private select = (selection: Set<string | number>) => this.setState({ selection })

  private editFilters = (key: string, value: any) => {
    const filters = Object.assign({}, this.state.filters) as any
    filters[key] = value
    this.setState({ filters })
    return Promise.resolve(null)
  }

  private finish = () => {
    const { selection, filters } = this.state
    assert(selection.size > 0, 'Expected a selection, found none')

    const taskOptions = cheapestFloorplanForEachTask(filterFloorplans(this.props.options, filters))
    const selectedTasks = taskOptions.filter(o => selection.has(this.getRowKey(o)))
    assert(selection.size === selectedTasks.length, `Expected ${selection.size} tasks, found ${selectedTasks.length}`)

    const message = createCrossSellMessage(selectedTasks, filters, new Date())

    /**
     * Return a QAResult like other QuickActions, including an Action
     */
    this.props.onChoose(this.getCrossSellQAResult(message, selectedTasks, filters))
  }

  private headers = ['', 'Task', 'City', 'Distance', 'Bed/ba', 'Rent', 'Available', 'Policy']

  private widths = ['10px', '120px', '115px', '70px', '70px', '70px', '90px', null]

  private cols = [
    (o: TaskOption) => (
      <span>
        <Icon name={propertyTypeToIconName(o.propertyType)} color={'silver'} />
        <Spacer width={8} />
      </span>
    ),
    (o: TaskOption) => o.taskName,
    (o: TaskOption) => (
      <span>
        <EllipsisSpan>{o.address.city}</EllipsisSpan> {o.address.state}
      </span>
    ),
    (o: TaskOption) => (o.distanceMi < 10 ? o.distanceMi.toFixed(1) : o.distanceMi.toFixed(0)) + ' mi',
    (o: TaskOption) => renderBedBathRange(o),
    (o: TaskOption) => `$${o.rent.min}${hasRange(o.rent) ? '+' : ''}`,
    (o: TaskOption) => o.bestCSO.floorplan.summary.availableOn,
    (o: TaskOption) => o.bestCSO.policy.policyTexts[this.state.policyCol as PolicyText] || 'Missing',
  ]

  /**
   * Wrap the message, selectedTasks, and filters into a single QuickActionResult. This helps us analyze
   * Cross Sell Search later: how many tasks we select, which filters we use, etc.
   *
   * TODO: we shouldn't need to construct cross-sell search results in the browser, we should serve them
   * from the backend as a well-typed action.
   */
  private getCrossSellQAResult(
    message: string,
    selectedTasks: TaskOption[],
    filters: CrossSellFilters
  ): QuickActionResult {
    const classification = 'SEND_CROSS_SELL' // msg has same classification as always
    const qaDisplayName = 'Cross Sell Search' // QA will have new displayName

    const sendMsgCmd = {
      // Action will have a single SendMessageCmd
      body: message,
      commandType: 'SEND_MESSAGE',
      messageInfo: {
        classification,
        outboundTags: [],
        questionsAnswered: [],
        rentQuoted: [],
        shiftIds: [],
        showingIds: [],
        tags: [],
      },
    }

    const logSummary = {
      // Action.summary records modal search filters and selected task IDs
      crossSellFilters: filters,
      tasksIds: selectedTasks.map(t => t.taskId),
      qaDisplayName,
    }

    return {
      __typename: 'QuickActionResult',
      action: {
        __typename: 'Action',
        actionName: ActionName.CROSS_SELL,
        preview: message,
        classification,
        sendQueue: SendQueue.IMMEDIATE,
        commandJsons: [JSON.stringify(sendMsgCmd)],
        uuid: null,
        logSummary: JSON.stringify(logSummary),
        hasArchiveCommand: false,
        hasSnoozeCommand: false,
        commandPreviews: [],
        commandTypes: [],
      },
      body: message,
      composerJson: null,
      displayName: qaDisplayName,
      hasError: false,
      snippet: message,
    }
  }
}

export function createCrossSellMessage(selectedTasks: TaskOption[], filters: CrossSellFilters, now: Date) {
  const cities = new Set(selectedTasks.map(cs => cs.address.city))

  // Produce options like
  // - "Farmbrooke Manor, 2br, 1 bath from $1100"
  // - "123 Main Street, Venice, 1br, 1 bath from $800"
  // - "123 Main Street, Inglewood, 1br, 1bath from $900, available Oct 1"
  const avails = new Set(selectedTasks.map(cs => describeAvail(cs, now, true)))
  const hasAvailFilter = filters.maxAvailableOn != null
  const addAvailPerOption = hasAvailFilter && (avails.size > 1 || selectedTasks.length === 1)
  const strs = selectedTasks.map(
    cs =>
      `${cs.taskName}${cities.size === 1 ? '' : ', ' + cs.address.city}, ` +
      `${describeBedBath(cs)} from $${cs.rent.min}` +
      `${addAvailPerOption ? ', ' + describeAvail(cs, now, false) : ''}`
  )

  // Produce a message. For example:
  //
  // I found a place that might work. Are you interested in a tour?
  // - Farmbrooke Manor, 2br, 1 bath from $1100
  // - 123 Main Street, 1br, 1 bath from $800
  // Both are available for move-in Nov 1.
  //
  let ret = `I found ${strs.length === 1 ? 'a place' : 'places'} that might work. Are you interested in a tour?\n`
  ret += strs.length === 1 ? strs[0] : strs.map(s => '- ' + s).join('\n')
  if (filters.maxAvailableOn != null && !addAvailPerOption) {
    const availForAllOptions = getOnlyElement(avails)
    ret += `\n${strs.length === 2 ? 'Both' : 'All'} are ${availForAllOptions}.`
  }
  return ret
}

function renderBedBathRange(o: TaskOption) {
  if (o.beds.min == null) {
    return 'unknown'
  }
  return `${o.beds.min}x${o.baths.min == null ? '—' : o.baths.min}${hasRange(o.beds) || hasRange(o.baths) ? '+' : ''}`
}

function hasRange(r: Range) {
  return (r.max || 0) > (r.min || 0)
}

function filterFloorplans(options: CrossSellOption[], filters: State['filters']) {
  const gt = (v?: number, min?: number) => min == null || (v != null && v >= min)
  const lt = (v: number, max?: number) => max == null || (v != null && v <= max)
  const lts = (v: string, max?: string) => max == null || (v != null && v <= max)

  return options
    .filter(o => gt(o.floorplan.bedrooms, filters.minBeds))
    .filter(o => gt(o.floorplan.bathrooms, filters.minBaths))
    .filter(o => lt(o.floorplan.summary.minRentUSD, filters.maxRent))
    .filter(o => lts(o.floorplan.summary.availableOn, filters.maxAvailableOn))
}

export function cheapestFloorplanForEachTask(options: CrossSellOption[]): TaskOption[] {
  const singleValRange = (v?: number): Range => ({ min: v, max: v })
  const updateRange = (v: number, r: Range) => {
    r.min = r.min == null ? v : Math.min(r.min, v)
    r.max = r.max == null ? v : Math.max(r.max, v)
  }

  const ret = [] as TaskOption[]
  for (const o of options) {
    const to = ret.find(t => t.taskId === o.taskId)
    if (to == null) {
      ret.push({
        taskId: o.taskId,
        propertyType: o.propertyType,
        taskName: o.taskName,
        address: o.address,
        distanceMi: o.distanceMi,
        bestCSO: o,
        beds: singleValRange(o.floorplan.bedrooms),
        baths: singleValRange(o.floorplan.bathrooms),
        rent: singleValRange(o.floorplan.summary.minRentUSD),
      })
    } else {
      if (to.rent.min && o.floorplan.summary.minRentUSD < to.rent.min) {
        to.bestCSO = o
      }
      updateRange(o.floorplan.bedrooms, to.beds)
      if (o.floorplan.bathrooms != null) {
        updateRange(o.floorplan.bathrooms, to.baths)
      }
      updateRange(o.floorplan.summary.minRentUSD, to.rent)
    }
  }
  return ret
}

/**
 * Returns '', 'studio', '1br', '1br, 1 bath', '2br, 1 bath', etc.
 */
function describeBedBath(option: TaskOption) {
  if (option.beds.min == null) {
    return ''
  }
  const beds = option.beds.min === 0 ? 'studio' : `${option.beds.min}br`
  return option.baths.min == null ? beds : `${beds}, ${option.baths.min} bath`
}

/**
 * Returns 'available now', 'available Dec 12', 'available for move-in now', 'available for move-in Dec 12'
 */
function describeAvail(option: TaskOption, now: Date, longForm?: boolean) {
  const availNoonUTC = new Date(option.bestCSO.floorplan.summary.availableOn + 'T12:00:00Z')
  const alreadyAvail = new Date(availNoonUTC) < now // not perfect, but close enough
  return `available${longForm ? ' for move-in' : ''} ${alreadyAvail ? 'now' : dateMDShortFmt(availNoonUTC)}`
}

// TODO: this should be auto-generated from the Java & GraphQL PolicyText enum
// if we ever use that enum in a query. This will probably happen with the Leasing Policy refactor.
enum PolicyText {
  SECTION_8 = 'SECTION_8',
  QUALIFICATIONS = 'QUALIFICATIONS',
  PETS = 'PETS',
  MOVE_IN_COST = 'MOVE_IN_COST',
  DISCOUNTS = 'DISCOUNTS',
  APPLICATION_DOCS = 'APPLICATION_DOCS',
  LEASE_TERMS = 'LEASE_TERMS',
  COSIGNERS = 'COSIGNERS',
  PARKING = 'PARKING',
  LAUNDRY = 'LAUNDRY',
  FURNISHED = 'FURNISHED',
  AMENITIES = 'AMENITIES',
  ADA_UNITS = 'ADA_UNITS',
  UTILITIES = 'UTILITIES',
}

const policyOptions = Object.keys(PolicyText).map(p => ({
  id: p,
  name: titleCase(p.replace(/_/g, ' ')).replace('Ada', 'ADA'),
}))

const fieldMinBed: Field<number> = {
  key: 'minBeds',
  isEditable: true,
  textFunc: val => (val ? `${val}+ beds` : 'Min beds?'),
  editTextFunc: val => (val ? '' + val : ''),
  placeholder: '2',
  validator: validate.integer(n => n >= 0 && n < 10).empty(''),
}

const fieldMinBaths: Field<number> = {
  key: 'minBaths',
  isEditable: true,
  textFunc: val => (val ? `${val}+ baths` : 'Min baths?'),
  editTextFunc: val => (val ? '' + val : ''),
  placeholder: '2',
  validator: validate.num(n => n >= 0 && n < 10).empty(''),
}

const fieldMaxRent: Field<number> = {
  key: 'maxRent',
  isEditable: true,
  textFunc: val => (val ? `$${val} or less` : 'Max rent?'),
  editTextFunc: val => (val ? '' + val : ''),
  placeholder: '1100',
  validator: validate.integer(n => n >= 100 && n < 10000).empty(''),
}

const fieldMaxAvailableOn: Field<string> = {
  key: 'maxAvailableOn',
  isEditable: true,
  textFunc: val => (val ? `Available by ${val}` : 'Move-in by?'),
  editTextFunc: val => val || '',
  placeholder: '2020-01-01',
  validator: validate.isoDate().empty(''),
}

const filterFields = [fieldMinBed, fieldMinBaths, fieldMaxRent, fieldMaxAvailableOn]

const Cell = styled.td`
  color: ${Colors.snow};
  padding: 1px 8px 1px 0;
`
